import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { copyToClipboard, useAuth, usePermission } from '@saas-kit/lib';
import {
  Button,
  ConfirmDialog,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';
import { DataTable, type DataTableColumn } from '@saas-kit/ui/DataTable';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { DisplayLinkCard } from './DisplayLinkCard';
import { KioskQrCard } from './KioskQrCard';
import type { QmsDisplay } from './types';
import { useDisplays } from './useDisplays';

/** Display screens list — the entry point for managing public lobby
 *  boards. Add / edit live on their own page (DisplayEditPage). */
export function DisplaysListPage(): JSX.Element {
  const { t } = useTranslation('qms');
  const navigate = useNavigate();
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canManage = usePermission('qms.displays.manage');
  const displays = useDisplays();
  const [pendingDelete, setPendingDelete] = useState<QmsDisplay | null>(null);
  const [pendingRotate, setPendingRotate] = useState<QmsDisplay | null>(null);

  const copyUrl = async (display: QmsDisplay): Promise<void> => {
    const url = `${window.location.origin}/qms/display/${display.slug}`;
    if (await copyToClipboard(url)) {
      toast.success(t('displays.copied'));
    } else {
      toast.error(t('displays.copy_failed'));
    }
  };

  const columns: DataTableColumn<QmsDisplay>[] = [
    { id: 'name', header: t('fields.name'), cell: (r) => r.name },
    { id: 'slug', header: t('displays.slug'), cell: (r) => r.slug },
    {
      id: 'target',
      header: t('displays.target'),
      cell: (r) => displays.resolveTarget(r),
    },
    {
      id: 'status',
      header: t('fields.status'),
      cell: (r) => (r.is_active ? t('status.active') : t('status.inactive')),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      exportSkip: true,
      cell: (r) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(`/qms/displays/${r.id}`)}
          >
            {t('actions.edit')}
          </Button>
          <Button variant="ghost" onClick={() => void copyUrl(r)}>
            {t('displays.copy_url')}
          </Button>
          <Button variant="ghost" onClick={() => setPendingRotate(r)}>
            {t('displays.rotate')}
          </Button>
          <Button variant="ghost" onClick={() => displays.toggleActive(r)}>
            {r.is_active ? t('actions.deactivate') : t('actions.activate')}
          </Button>
          <Button variant="ghost" onClick={() => setPendingDelete(r)}>
            {t('actions.delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (!selectedTenant) {
    return (
      <TenantRequiredState
        title={t('tenant_required.title')}
        body={t('tenant_required.body')}
        cta={t('tenant_required.cta')}
      />
    );
  }
  if (!canManage) {
    return <ErrorBanner>{t('errors.displays_denied')}</ErrorBanner>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={t('displays.list_title')}
          description={t('displays.list_subtitle')}
        />
        <Button onClick={() => navigate('/qms/displays/new')}>
          {t('displays.add')}
        </Button>
      </div>

      {displays.revealed ? (
        <DisplayLinkCard
          link={displays.revealed}
          onDismiss={displays.dismissRevealed}
        />
      ) : null}

      <KioskQrCard />

      <DataTable
        data={displays.displays}
        columns={columns}
        rowKey={(r) => r.id}
        selectable
        isLoading={displays.isLoading}
        error={displays.isError ? t('errors.displays_denied') : null}
        emptyText={t('displays.empty')}
        bulkActions={[
          {
            id: 'delete',
            label: t('actions.delete'),
            variant: 'destructive',
            onRun: (rows) => displays.removeMany(rows.map((r) => r.id)),
          },
        ]}
      />

      {pendingRotate ? (
        <ConfirmDialog
          title={t('displays.rotate_confirm_title')}
          body={t('displays.rotate_confirm_body')}
          confirmLabel={t('displays.rotate')}
          cancelLabel={t('confirm.cancel')}
          isBusy={displays.rotating}
          onCancel={() => setPendingRotate(null)}
          onConfirm={() => {
            displays.rotate(pendingRotate.id);
            setPendingRotate(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title={t('confirm.delete_title')}
          body={t('confirm.delete_body')}
          confirmLabel={t('confirm.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={displays.removing}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            displays.remove(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      ) : null}
    </div>
  );
}
